import Swal from "sweetalert2";
import { api } from "@service/api";
import { Input } from "@components/forms/input";
import { Pagination } from "@components/pagination";
import { ICustomerPublicDTO } from "@hooks/dtos/user-dto";
import { formatName } from "@hooks/functions/formatName";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useEffect, useState } from "react";

interface IProps {
  raffleKey: string;
  isOpen: boolean;
  onClose: (created?: boolean) => void;
}

interface ISearchResponse {
  customers: ICustomerPublicDTO[];
  page: number;
  totalPages: number;
}

export function ModalRaffleAffiliateCreate({
  raffleKey,
  isOpen,
  onClose,
}: IProps) {
  // Stores and states
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();
  const [animate, setAnimate] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<ICustomerPublicDTO[]>([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<ICustomerPublicDTO | null>(null);

  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [take] = useState<number>(10);

  const [percent, setPercent] = useState<number | string>("");

  // Search customers by name or atSign
  const handleSearchCustomers = async (
    term: string,
    currentPage: number = 1,
  ) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      setTotalPages(0);
      return;
    }

    try {
      setIsSearching(true);
      const params = `?search=${encodeURIComponent(term)}&page=${currentPage}&take=${take}`;
      const result = await api.get<ISearchResponse>(
        `/misc/customer/search${params}`,
      );

      setSearchResults(result.data.customers || []);
      setTotalPages(result.data.totalPages || 0);
      setIsSearching(false);
    } catch (err: any) {
      pushToastify({
        message: err?.response?.data.message ?? "Erro ao buscar usuários!",
        type: "error",
        theme: "dark",
      });
      setIsSearching(false);
      setSearchResults([]);
      setTotalPages(0);
    }
  };

  // Handle customer selection
  const handleSelectCustomer = (customer: ICustomerPublicDTO) => {
    setSelectedCustomer(customer);
    setSearchTerm("");
    setSearchResults([]);
    setPage(1);
    setTotalPages(0);
  };

  // Clear selected customer and return to search
  const handleBackToSearch = () => {
    setSelectedCustomer(null);
    setPercent(0);
  };

  // Validate percentage input
  const isValidPercent = (): boolean => {
    const numPercent = Number(percent);
    if (percent === "" || isNaN(numPercent)) {
      pushToastify({
        message: "Por favor, informe o percentual de comissão desejado",
        type: "warning",
        theme: "dark",
      });
      return false;
    }
    if (numPercent <= 0) {
      pushToastify({
        message: "O percentual deve ser maior que zero",
        type: "warning",
        theme: "dark",
      });
      return false;
    }
    if (numPercent > 100) {
      pushToastify({
        message: "O percentual não pode ser maior que 100%",
        type: "warning",
        theme: "dark",
      });
      return false;
    }
    return true;
  };

  // Save new affiliate
  const handleSaveAffiliate = async () => {
    if (!selectedCustomer) {
      pushToastify({
        message: "Selecione um usuário antes de salvar",
        type: "warning",
        theme: "dark",
      });
      return;
    }

    if (!isValidPercent()) return;

    Swal.fire({
      title: "Criar afiliado?",
      text: `${formatName(selectedCustomer.name)} será adicionado como afiliado`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, criar!",
      cancelButtonText: "Cancelar",
    }).then(async (result: { isConfirmed: boolean }) => {
      if (result.isConfirmed) {
        try {
          setIsSaving(true);
          const data = {
            affiliate: selectedCustomer.id,
            percent: percent,
          };
          const params = `?key=${raffleKey}`;
          const result = await api.post(
            `/customer/raffle/affiliate${params}`,
            data,
          );

          pushToastify({
            message: "Solicitação enviada com sucesso!",
            type: "success",
            theme: "dark",
          });

          setIsSaving(false);
          handleCancel(true);
        } catch (err: any) {
          pushToastify({
            message: err?.response?.data.message ?? "Erro ao criar afiliado!",
            type: "error",
            theme: "dark",
          });
          setIsSaving(false);
        }
      }
    });
  };

  // Close modal
  function handleCancel(created: boolean = false) {
    setAnimate(false);
    setSelectedCustomer(null);
    setSearchTerm("");
    setSearchResults([]);
    setPage(1);
    setTotalPages(0);
    setPercent(0);
    setTimeout(() => onClose(created), 300);
  }

  // Handle escape key
  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      if (selectedCustomer) {
        handleBackToSearch();
      } else {
        handleCancel();
      }
    }
  }

  // Debounce search on term change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        setPage(1); // Reset to page 1 when search term changes
        handleSearchCustomers(searchTerm, 1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search when page changes
  useEffect(() => {
    if (searchTerm.length >= 2 && page > 0) {
      handleSearchCustomers(searchTerm, page);
    }
  }, [page]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="staticBackdropLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="contentModallLabel">
                {selectedCustomer
                  ? "Configurar Afiliado"
                  : "Adicionar Novo Afiliado"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => handleCancel()}
              ></button>
            </div>

            <div className="modal-body">
              {!selectedCustomer ? (
                <>
                  {/* Search Section */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">
                      Buscar Usuário
                    </label>
                    <div className="position-relative">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Digite o nome ou @arroba..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                      {isSearching && (
                        <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                          <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Buscando...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <small className="text-muted">
                      Digite pelo menos 2 caracteres para buscar
                    </small>
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="border rounded p-2 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          Página {page} de {totalPages}
                        </small>
                      </div>
                      <div
                        className="list-group mb-3"
                        style={{ maxHeight: "300px", overflowY: "auto" }}
                      >
                        {searchResults.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                            onClick={() => handleSelectCustomer(customer)}
                          >
                            {customer.avatar ? (
                              <img
                                src={customer.avatar}
                                alt={customer.name}
                                className="rounded-circle"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white fw-bold"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  fontSize: "16px",
                                }}
                              >
                                {customer.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-grow-1 text-start">
                              <div className="fw-semibold">
                                {formatName(customer.name)}
                              </div>
                              <small className="text-muted">
                                @{customer.atSign}
                              </small>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <Pagination
                          page={page}
                          setPage={setPage}
                          totalPages={totalPages}
                          maxPageShow={3}
                          loading={isSearching}
                        />
                      )}
                    </div>
                  )}

                  {searchTerm.length >= 2 &&
                    !isSearching &&
                    searchResults.length === 0 && (
                      <div className="alert alert-info">
                        Nenhum usuário encontrado com "{searchTerm}"
                      </div>
                    )}
                </>
              ) : (
                <>
                  {/* Back Button */}
                  <div className="mb-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={handleBackToSearch}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Voltar para busca
                    </button>
                  </div>

                  {/* Selected User Info */}
                  <div className="text-center mb-4 pb-3 border-bottom">
                    {selectedCustomer.avatar ? (
                      <img
                        src={selectedCustomer.avatar}
                        alt={selectedCustomer.name}
                        className="rounded-circle mb-3"
                        style={{
                          width: "80px",
                          height: "80px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-circle bg-primary d-inline-flex align-items-center justify-content-center text-white fw-bold mb-3"
                        style={{
                          width: "80px",
                          height: "80px",
                          fontSize: "32px",
                        }}
                      >
                        {selectedCustomer.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <h4 className="mb-1">
                      {formatName(selectedCustomer.name)}
                    </h4>
                    <p className="fs-5 text-muted mb-0">
                      @{selectedCustomer.atSign}
                    </p>
                  </div>

                  {/* Editable Fields */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Percentual de Comissão (%)
                    </label>
                    <Input
                      animated
                      label="Percentual de Comissão (%)"
                      placeholder=""
                      type="number"
                      min="0"
                      max="100"
                      step="1.0"
                      value={percent}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPercent(value === "" ? "" : Number(value));
                      }}
                    />
                    <small className="ms-1 text-muted">
                      * Comissão em percentual do valor de cada ticket vendido.
                    </small>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              {selectedCustomer ? (
                <>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleSaveAffiliate}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Salvando...
                      </>
                    ) : (
                      "Criar Afiliado"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleCancel()}
                    disabled={isSaving}
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleCancel()}
                >
                  Fechar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
